/**
 * The AI team in one glance — what the Team item in the top bar and the team
 * sub-nav show. Counts come from `GET /ai-team/attention` (the same cache
 * entry the triage panel reads, so one request serves both); the working
 * count from the employee cards. Admin-only: call it only once `isAdmin` is
 * `true`, the endpoints are admin routes.
 */
import { adminApi } from "../api/admin";
import {
  teamAttentionBreakdown,
  teamNeedsYouCount,
  teamPresence,
  type TeamAttentionCounts,
  type TeamPresence,
} from "../lib/aiTeam";

export interface TeamStatus {
  presence: TeamPresence;
  needsYou: number;
  counts: TeamAttentionCounts | null;
  breakdown: string;
}

const ALERT_STATES = new Set(["login_expired", "stuck", "deaf", "offline", "backlogged"]);

export function useTeamStatus(): TeamStatus {
  const attention = adminApi.aiTeam.attention();
  const agents = adminApi.aiTeam.agents();
  const cards = agents.data ?? [];
  const working = cards.filter((a) => a.state === "working").length;

  let counts: TeamAttentionCounts | null = attention.data?.counts ?? null;
  if (!counts && agents.data) {
    // Attention not loaded (yet, or failing): fall back to what the cards know.
    counts = {
      agents_alerting: cards.filter((a) => ALERT_STATES.has(a.state)).length,
      pending_questions: cards.reduce((n, a) => n + a.open_questions, 0),
      pending_reviews: 0,
    };
  }
  const needsYou = counts ? teamNeedsYouCount(counts) : 0;
  return {
    presence: teamPresence(needsYou, working),
    needsYou,
    counts,
    breakdown: counts ? teamAttentionBreakdown(counts, working) : "AI team",
  };
}
